import moment from "moment";
import _ from "lodash";
import { startCase, map } from "lodash";
import { TRIGGER_NAME, ACTION_NAME, SCHEDULINGS_NAME } from "@/constants/dynamicSchedulingConstants";

const TRIGGERS = {
  [TRIGGER_NAME.ACTIVITY_COMPLETED]: {
    text: "Activity is Completed",
    hasStep: false,
    hasField: false,
    hasMilestones: false
  },
  [TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED]: {
    text: "Step in Activity is Completed",
    hasStep: true,
    hasField: false,
    hasMilestones: false
  },
  [TRIGGER_NAME.ACTIVITY_MISSED]: {
    text: "Activity was Missed",
    hasStep: false,
    hasField: false,
    hasMilestones: false
  },
  [TRIGGER_NAME.PARTICIPANT]: {
    text: "Participant Information",
    field: {
      gender: "Gender",
      birthDate: "Date of Birth",
      dob: "Date of Birth",
      dobWithMonthYear: "Date of Birth",
      dobWithYear: "Date of Birth",
      language: "Language",
      country: "Country",
      ageRange: "Age Range"
    },
    operator: {
      "!=": "Does Not Equal",
      "=": "Is Equal To",
      ">": "Is Greater Than",
      "<": "Is Less Than"
    }
  },
  [TRIGGER_NAME.MILESTONE_COMPLETED]: {
    text: "Milestone(s) Completed",
    hasStep: false,
    hasField: false,
    hasMilestones: true
  }
};
const ACTIONS = {
  [ACTION_NAME.ADD_AN_ACTIVITY]: { text: "Add an Activity" },
  [ACTION_NAME.REMOVE_AN_ACTIVITY]: { text: "Remove an Activity" },
  [ACTION_NAME.NOTIFY_SITE_TEAM_MEMBER]: { text: "Notify Research Team" }
};
const SCHEDULINGS = {
  [SCHEDULINGS_NAME.IMMEDIATELY]: { text: "Immediately" },
  [SCHEDULINGS_NAME.NO_OF_DAYS]: { text: "Number of Days" },
  [SCHEDULINGS_NAME.MILESTONE]: { text: "Milestones" },
  [SCHEDULINGS_NAME.CUSTOM]: { text: "Custom Frequency" }
};
const ABBREVATION_MAPPINGS = {
  Pi: "Principal Investigator",
  SubPi: "Sub-Investigator",
  StudyCoordinator: "Study Coordinator",
  SiteDataLock: "Site-Data Lock",
  HomeHealth: "Home Health",
  Observer: "Observer",
  CallCenter: "Call Center",
  Cra: "Clinical Research Associate (Blinded)",
  CraUB: "Clinical Research Associate (Unblinded)",
  Dm: "Data Management",
  Quality: "Quality",
  StudyAdmin: "Study Admin",
  SystemAdmin: "System Admin"
};
const STEP_TYPES = [
  {
    value: "SurveyInstruction",
    label: "Instruction screen",
    facingType: "both"
  },
  { value: "CustomScale", label: "Custom scale", facingType: "both" },
  { value: "DateTime", label: "Date / Time", facingType: "both" },
  { value: "DropDown", label: "Drop-down", facingType: "both" },
  {
    value: "GPSkin",
    label: "gpskin Barrier device scan",
    facingType: "participant"
  },
  { value: "ImageCapture", label: "Image capture", facingType: "participant" },
  { value: "Image", label: "Image choice", facingType: "both" },
  { value: "Index", label: "Index", facingType: "site" },
  { value: "Log", label: "Log Entry", facingType: "site" },
  { value: "Measurements", label: "Measurements", facingType: "site" },
  {
    value: "MedicationSelector",
    label: "Medication Selector",
    facingType: "participant"
  },
  { value: "MultiChoice", label: "Multiple choice", facingType: "both" },
  { value: "Number", label: "Number Response", facingType: "both" },
  { value: "NumberScale", label: "Number Scale", facingType: "both" },
  { value: "Text", label: "Text Response", facingType: "both" },
  { value: "ValidatedEntry", label: "Validated Entry", facingType: "site" },
  { value: "VAS", label: "Visual analog scale", facingType: "participant" },
  { value: "Boolean", label: "Yes or No", facingType: "both" },
  {
    value: "WongBaker",
    label: "Wong-Baker FACES Pain Rating Scale",
    facingType: "participant"
  },
  {
    value: "AudioRecording",
    label: "Audio Recording",
    facingType: "participant"
  },
  {
    value: "VideoRecording",
    label: "Video Recording",
    facingType: "participant"
  }
];

export default class DynamicSchedulingService {
  studyDetails = null;
  dynamicSchedules = null;
  taskDetails = null;
  triggers = TRIGGERS;
  actions = ACTIONS;
  schedulings = SCHEDULINGS;
  stepTypes = STEP_TYPES;
  registrationConfigDetails = null;

  setStudyDetails(studyData) {
    this.studyDetails = studyData;
  }

  setDynamicSchedules(taskInfo) {
    this.taskDetails = taskInfo;
  }

  isStepInActivityCompleted(key) {
    return key === TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED;
  }

  convertStepTypeCamelCaseToNormalCase(str) {
    const step = this.stepTypes.filter(step => {
      return step.value === str;
    });
    return step && step[0] && step[0].label;
  }

  getStepInActivityStepTitle(condition) {
    const selectedQuestion =
      this.taskDetails.questions &&
      this.taskDetails.questions.filter(que => {
        return que.id === condition.questionId;
      });
    if (selectedQuestion.length) {
      const stepSequence = selectedQuestion && selectedQuestion[0] && selectedQuestion[0].stepSequence ? selectedQuestion[0].stepSequence : selectedQuestion[0].sequence;
      return selectedQuestion && selectedQuestion[0] && `Step ${stepSequence} - ${this.convertStepTypeCamelCaseToNormalCase(selectedQuestion[0].type)}`;
    }
  }

  changeDropdownValuesBasedOnResponseType(condition) {
    if (condition && condition.trigger === TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED) {
      if (condition.responseText) {
        /* When response is taken from Step options */
        return condition.responseText;
      } else if (condition.anyResponse) {
        /* When response is 'Any Response' */
        return "Any Response";
      } else if (condition.noResponse) {
        /* When response is 'No Response' */
        return "No Response";
      } else {
        /* When there is a change in Step */
        return null;
      }
    }
  }

  isParticipant(key) {
    return key === TRIGGER_NAME.PARTICIPANT;
  }

  isMilestoneComplete(key) {
    return key === TRIGGER_NAME.MILESTONE_COMPLETED;
  }

  forActionIsAddAnActivity(key) {
    return key === ACTION_NAME.ADD_AN_ACTIVITY;
  }

  forActionIsRemovedAnActivity(key) {
    return key === ACTION_NAME.REMOVE_AN_ACTIVITY;
  }

  forActionIsNotifySiteTeam(key) {
    return key === ACTION_NAME.NOTIFY_SITE_TEAM_MEMBER;
  }

  capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  convertCamelCaseToNormalCase(str) {
    return startCase(str);
  }

  getGenderText(value) {
    const genderObj = this.getTextFromMLlookupValue(value, "gender");
    return genderObj ? (genderObj["values"].includes("Other") ? "Other" : genderObj["values"]) : this.capitalizeFirstLetter(value);
  }

  getAgeRangeText(value) {
    const ageRangeObj = this.getTextFromMLlookupValue(value, "ageRange");
    return ageRangeObj ? ageRangeObj["values"] : value;
  }

  getTextFromMLlookupValue(id, fieldType) {
    const lookupValues = this.registrationConfigDetails.find(config => {
      return config.key == fieldType;
    }).lookupExpectedValue;
    return (
      lookupValues &&
      lookupValues.length &&
      lookupValues.find(obj => {
        return id === obj["identifier"];
      })
    );
  }

  getReadableDOB(val) {
    if (val == null || (val && val.toUpperCase() === "INVALID DATE")) {
      return "";
    }

    const validDate = val.match(/^((0[1-9]|1[0-9]|2[0-9]|3[0-1])[\s](JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[\s](\d{4}))$/);

    if (validDate) {
      return val;
    }

    return moment(val, "DD/MM/YYYY")
      .format("DD MMM YYYY")
      .toUpperCase();
  }

  getLanguageName(langId) {
    let displayName = null;
    this.studyDetails.studyLanguage &&
      this.studyDetails.studyLanguage.forEach(lang => {
        if (lang.language_id === langId) {
          displayName = lang.display_name;
        }
      });
    return displayName;
  }

  getCountryName(countryId) {
    let displayName = null;
    this.studyDetails.countryList &&
      this.studyDetails.countryList.forEach(country => {
        if (country.countryId === countryId) {
          displayName = country.countryName;
        }
      });
    return displayName;
  }

  getMilestones(milestones) {
    return map(milestones, "displayName").join(", ");
  }

  getMilestonesForSchedulings(schedule) {
    let chosenMilestones = [];
    if (schedule.type === SCHEDULINGS_NAME.MILESTONE) {
      if (schedule.milestones && schedule.milestones.length) {
        for (const mileDay of schedule.milestones) {
          for (const mileDetails of schedule.milestonesList) {
            if (mileDetails.milestoneDay == mileDay) {
              chosenMilestones.push(mileDetails);
              break;
            }
          }
        }
      }
    }
    chosenMilestones = _.sortBy(chosenMilestones, "milestoneType");
    return this.getMilestones(chosenMilestones);
  }

  getActivityForSchedulings(activity) {
    let chosenActivities = [];
    if (activity.tasks && activity.tasks.length) {
      for (const task of activity.tasks) {
        chosenActivities.push(task.taskTitle);
      }
    }
    chosenActivities = _.sortBy(chosenActivities, "taskType");
    return chosenActivities.join(", ");
  }

  getDisplayMilestoneDay(milestoneDayOld) {
    let milestoneDay = milestoneDayOld;
    if (milestoneDay != null && this.studyDetails.screeningMilestoneAllowed) {
      // For screening milestones
      return (milestoneDay -= this.studyDetails.screeningDuration);
    } else {
      // For regular milestones
      return milestoneDay;
    }
  }

  getSiteTeamMemberNames(role) {
    const allMembersList = [];
    if (role.studyTeam) {
      for (const member of role.studyTeam) {
        allMembersList.push(ABBREVATION_MAPPINGS[member]);
      }
    }

    if (role.siteTeam) {
      for (const member of role.siteTeam) {
        allMembersList.push(ABBREVATION_MAPPINGS[member]);
      }
    }
    return allMembersList.join(", ");
  }

  showCompletionWindow(actions) {
    let hasRemoveAnActivityConfiguredOnly = true;
    if (actions && actions.length) {
      actions.forEach(a => {
        if (a.type === ACTION_NAME.ADD_AN_ACTIVITY) {
          hasRemoveAnActivityConfiguredOnly = false;
        }
      });
    }
    return hasRemoveAnActivityConfiguredOnly;
  }

  getCompletionWindow(schedule) {
    return schedule["completionDuration"] || schedule["completionWindow"];
  }
}
